/**
 * Weblab Visual Dashboard plugin — entry point and registration.
 *
 * Shows the daily SP/SB revenue impact of weblabs from
 * `sandbox.finn_preprocessed_data_weblabs` (server-side aggregates; see
 * useWeblabsData). Registered in dashboardRegistry so it appears as a sub-item
 * under the "Dashboard" selector, next to Weblab Tracker.
 *
 * Self-contained like the Weblab Tracker plugin: FilterPanel is a no-op and
 * useData is a stub, because WeblabsReportContent owns its own filters and data
 * fetching. Visible to everyone (no gate in visibleDashboardsFor).
 */

import React from "react";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import type {
  DashboardPlugin,
  PluginDataResult,
  PluginFilterPanelProps,
} from "../../types";
import WeblabsReportContent from "./WeblabsReportContent";

/** DashboardShell requires a FilterPanel; ours lives inside ReportContent. */
const WeblabsNullFilterPanel: React.FC<PluginFilterPanelProps> = () => null;

/** The dashboard self-fetches, so the shell-level data hook is a no-op. */
function useNoWeblabsData(): PluginDataResult {
  return { data: null, isLoading: false, error: null, refetch: () => {} };
}

export const weblabsVisualDashboard: DashboardPlugin = {
  id: "weblabs-visual",
  displayName: "Weblab Visual",
  icon: React.createElement(BiotechOutlinedIcon),
  description: "Daily SP/SB revenue impact of weblabs",

  FilterPanel: WeblabsNullFilterPanel,
  ReportContent: WeblabsReportContent,
  useData: useNoWeblabsData,

  supportsComparison: false,
};

export default weblabsVisualDashboard;
